import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/shared/components/ui/navigation-menu';
import { navigationItems } from './NavigationData';

interface MainNavigationProps {
  preserveBrandUrl: (href: string) => string;
}

export default function MainNavigation({ preserveBrandUrl }: MainNavigationProps) {
  return (
    <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList>
        {navigationItems.map((item) => (
          <NavigationMenuItem key={item.title}>
            {item.items ? (
              <>
                <NavigationMenuTrigger className="text-gray-700 hover:text-blue-700 font-medium">
                  {item.title}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="p-6 w-[600px] lg:w-[800px]">
                    {/* Header with description */}
                    <div className="mb-6 pb-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                    </div>
                    
                    {/* Services Grid */}
                    {(item.title === 'Services' || item.title === 'Programs & Plans') && (
                      <div className="grid lg:grid-cols-3 gap-3">
                        {item.items.map((category) => (
                          <NavigationMenuLink
                            key={category.title}
                            href={preserveBrandUrl(category.href)}
                            className="block p-3 rounded-lg hover:bg-blue-50 transition-colors group border border-gray-100"
                          >
                            <div className="flex items-start space-x-3">
                              <div className="text-2xl flex-shrink-0">{category.icon}</div>
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-gray-900 group-hover:text-blue-700 text-sm">
                                  {category.title}
                                </div>
                                <div className="text-xs text-gray-600 mt-1 line-clamp-2">
                                  {category.description}
                                </div>
                                {category.features && (
                                  <div className="mt-2 text-xs text-gray-500">
                                    {category.features.slice(0, 2).join(' • ')}
                                  </div>
                                )}
                                {category.price && (
                                  <div className="mt-1 text-xs font-semibold text-green-600">
                                    {category.price}
                                  </div>
                                )}
                              </div>
                            </div>
                          </NavigationMenuLink>
                        ))}
                      </div>
                    )}

                    {/* Technology and Specialist Services Grid */}
                    {(item.title === 'Smart Technology' || item.title === 'Specialist Solutions') && (
                      <div className="grid lg:grid-cols-2 gap-3">
                        {item.items.map((tech) => (
                          <NavigationMenuLink
                            key={tech.title}
                            href={preserveBrandUrl(tech.href)}
                            className="block p-3 rounded-lg hover:bg-blue-50 transition-colors group border border-gray-100"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="text-xl">{tech.icon}</div>
                              <div>
                                <div className="font-medium text-gray-900 group-hover:text-blue-700 text-sm">
                                  {tech.title}
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  {tech.description}
                                </div>
                                {tech.price && (
                                  <div className="mt-1 text-xs font-semibold text-green-600">
                                    {tech.price}
                                  </div>
                                )}
                              </div>
                            </div>
                          </NavigationMenuLink>
                        ))}
                      </div>
                    )}
                  </div>
                </NavigationMenuContent>
              </>
            ) : (
              <NavigationMenuLink 
                href={preserveBrandUrl(item.href)}
                className="text-gray-700 hover:text-blue-700 font-medium px-3 py-2 rounded-md transition-colors"
              >
                {item.title}
              </NavigationMenuLink>
            )}
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );
}